/**
 * CheckboxField Component
 *
 * Tyler Forge checkbox adapter for React Hook Form
 */

import { forwardRef } from 'react';
import { type FieldError } from 'react-hook-form';
import '@tylertech/forge/checkbox';
import './CheckboxField.css';

export interface CheckboxFieldProps {
  /** Field name (required for React Hook Form) */
  name: string;
  /** Checkbox label */
  label: string;
  /** Is field disabled */
  disabled?: boolean;
  /** Helper text */
  helperText?: string;
  /** Validation error from React Hook Form */
  error?: FieldError;
  /** Default checked state */
  defaultChecked?: boolean;
  /** Checked state (controlled) */
  checked?: boolean;
  /** Change handler */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Blur handler */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Checkbox field adapter for React Hook Form
 *
 * Wraps Tyler Forge checkbox component with React Hook Form support
 *
 * @example
 * ```tsx
 * <CheckboxField
 *   label="Mark as deprecated"
 *   {...register('isDeprecated')}
 *   error={errors.isDeprecated}
 * />
 * ```
 */
export const CheckboxField = forwardRef<HTMLElement, CheckboxFieldProps>(
  (
    {
      name,
      label,
      disabled = false,
      helperText,
      error,
      defaultChecked,
      checked,
      onChange,
      onBlur,
      className,
    },
    ref
  ) => {
    const hasError = Boolean(error);
    const CheckboxElement = 'forge-checkbox' as React.ElementType;

    return (
      <div
        className={`checkbox-field-wrapper ${hasError ? 'checkbox-field-wrapper--error' : ''} ${className || ''}`}
      >
        <CheckboxElement
          ref={ref}
          id={name}
          name={name}
          disabled={disabled}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={hasError ? 'true' : undefined}
        >
          <label htmlFor={name} className="checkbox-field-label">
            {label}
          </label>
        </CheckboxElement>
        {hasError && error?.message && <div className="form-error-message">{error.message}</div>}
        {!hasError && helperText && <div className="form-helper-text">{helperText}</div>}
      </div>
    );
  }
);

CheckboxField.displayName = 'CheckboxField';
